import {
    getApplicationNotificationTemplate,
    isApplicationNotificationTemplateCode,
} from './notification-template-catalog.js';
import {
    getNotificationEventDefinition,
    NOTIFICATION_POLICY_SYSTEM_VARIABLES,
} from './notification-routing.registry.js';

export const NOTIFICATION_TEMPLATE_BINDING_KINDS = Object.freeze([
    'application',
    'event',
    'workflow',
]);

const GROUP_LABELS = Object.freeze({
    entity: { tr: 'Kayıt alanları', en: 'Record fields' },
    system: { tr: 'Sistem', en: 'System' },
    identity: { tr: 'Kimlik / gezinme', en: 'Identity / navigation' },
    application: { tr: 'Uygulama değişkenleri', en: 'Application variables' },
    workflow: { tr: 'Şablon değişkenleri', en: 'Template variables' },
});

const NAVIGATION_HELP = Object.freeze({
    listRowHighlight: {
        tr: 'Tıklanınca ilgili liste sayfasına gider ve kaydı satır olarak vurgular. Şablonda actionUrl yazılmaz.',
        en: 'Click opens the list page and highlights the row. Templates must not set actionUrl.',
    },
    listPageOnly: {
        tr: 'Tıklanınca yalnızca liste sayfası açılır (kayıt silinmiş olabilir).',
        en: 'Click opens the list page only (the record may already be deleted).',
    },
});

function cloneLocalized(value, fallbackEn) {
    if (value && typeof value === 'object' && value.tr && value.en) {
        return { tr: value.tr, en: value.en };
    }
    return { tr: fallbackEn, en: fallbackEn };
}

function asContractVariable(entry, { insertable = true, group = 'entity' } = {}) {
    const key = typeof entry === 'string' ? entry : entry.key;
    return {
        key,
        type: typeof entry === 'string' ? 'string' : (entry.type || 'string'),
        label: typeof entry === 'string'
            ? { tr: key, en: key }
            : cloneLocalized(entry.label, key),
        description: typeof entry === 'string'
            ? null
            : (entry.description ? cloneLocalized(entry.description, '') : null),
        example: typeof entry === 'string' ? '' : (entry.example ?? ''),
        required: typeof entry === 'string' ? false : entry.required !== false,
        insertable,
        group,
    };
}

/**
 * Build the admin-facing variable contract for an application code or curated event.
 */
export function buildVariableContract({ eventName = null, code = null, declaredVariables = [] } = {}) {
    if (eventName) {
        const definition = getNotificationEventDefinition(eventName);
        if (!definition) return null;

        const identityKeys = new Set(['resourceId']);
        const entityVariables = (definition.variables || [])
            .filter((variable) => !identityKeys.has(variable.key))
            .map((variable) => asContractVariable(variable, { group: 'entity' }));
        const identityVariables = (definition.variables || [])
            .filter((variable) => identityKeys.has(variable.key))
            .map((variable) => asContractVariable(variable, { group: 'identity', insertable: true }));

        if (!identityVariables.length) {
            identityVariables.push(asContractVariable({
                key: 'resourceId',
                type: 'string',
                label: { tr: 'Kayıt kimliği', en: 'Record id' },
                description: {
                    tr: 'MongoDB kayıt kimliği. Tıklama hedefi navigation.entityId ile aynıdır; şablonda isteğe bağlı gösterilir.',
                    en: 'MongoDB record id. Same value as navigation.entityId for clicks; optional in copy.',
                },
                example: definition.previewFixture?.resource?.id || `fixture-${definition.resource?.type || 'record'}`,
                required: false,
            }, { group: 'identity', insertable: true }));
        }

        const navKind = definition.previewFixture?.navigation?.kind || 'listRowHighlight';
        return {
            source: {
                kind: 'event',
                eventName: definition.name,
                label: definition.label,
            },
            groups: [
                {
                    id: 'entity',
                    label: GROUP_LABELS.entity,
                    variables: entityVariables,
                },
                {
                    id: 'system',
                    label: GROUP_LABELS.system,
                    variables: NOTIFICATION_POLICY_SYSTEM_VARIABLES.map((variable) => (
                        asContractVariable(variable, { group: 'system' })
                    )),
                },
                {
                    id: 'identity',
                    label: GROUP_LABELS.identity,
                    variables: identityVariables,
                    navigation: {
                        pageKey: definition.resource.pageKey,
                        kind: navKind,
                        help: NAVIGATION_HELP[navKind] || NAVIGATION_HELP.listRowHighlight,
                    },
                },
            ],
            sampleVariables: {
                ...(definition.previewFixture?.variables || {}),
                title: 'Sample title',
                body: 'Sample body',
                tenantName: 'Demo Tenant',
                currentYear: new Date().getUTCFullYear(),
            },
            allowedKeys: [
                ...entityVariables.map(({ key }) => key),
                ...identityVariables.map(({ key }) => key),
                ...NOTIFICATION_POLICY_SYSTEM_VARIABLES.map(({ key }) => key),
            ],
            lockedKeys: [
                ...entityVariables.map(({ key }) => key),
                ...identityVariables.map(({ key }) => key),
            ],
        };
    }

    if (code && isApplicationNotificationTemplateCode(code)) {
        const template = getApplicationNotificationTemplate(code);
        const variables = template.variables.map((variable) => (
            asContractVariable(variable, { group: 'application' })
        ));
        return {
            source: {
                kind: 'application',
                code: template.code,
                category: template.category,
            },
            groups: [
                {
                    id: 'application',
                    label: GROUP_LABELS.application,
                    variables,
                },
            ],
            sampleVariables: Object.fromEntries(
                template.variables.map((variable) => [variable.key, variable.example ?? '']),
            ),
            allowedKeys: variables.map(({ key }) => key),
            lockedKeys: variables.map(({ key }) => key),
        };
    }

    const keys = [...new Set((declaredVariables || []).map(String).filter(Boolean))];
    return {
        source: { kind: 'workflow' },
        groups: [
            {
                id: 'workflow',
                label: GROUP_LABELS.workflow,
                variables: keys.map((key) => asContractVariable(key, { group: 'workflow' })),
            },
        ],
        sampleVariables: Object.fromEntries(keys.map((key) => [key, `sample-${key}`])),
        allowedKeys: keys,
        lockedKeys: [],
    };
}

export function resolveNotificationTemplateBinding({ code, binding } = {}) {
    if (binding?.kind === 'event' && binding.eventName) {
        return {
            kind: 'event',
            eventName: String(binding.eventName),
        };
    }
    if (binding?.kind === 'application' || isApplicationNotificationTemplateCode(code)) {
        return { kind: 'application', eventName: null };
    }
    if (binding?.kind === 'workflow') {
        return { kind: 'workflow', eventName: null };
    }
    return { kind: 'workflow', eventName: null };
}

export function lockedVariablesForBinding(binding, code) {
    const resolved = resolveNotificationTemplateBinding({ code, binding });
    if (resolved.kind === 'event') {
        const contract = buildVariableContract({ eventName: resolved.eventName });
        return contract?.lockedKeys || [];
    }
    if (resolved.kind === 'application') {
        const contract = buildVariableContract({ code });
        return contract?.lockedKeys || [];
    }
    return null;
}
